import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import routes, { RouteInterface } from './routes';
import PublicLayout from './PublicLayout';
import PrivateLayout from './Layout';
import AuthHandler from './components/AuthHandler/AuthHandler';
import GlobalProvider from './Context';
import React, { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import useGlobalButtonTracker from './utils/buttonClickEvent';
import axios from 'axios';
import { message } from 'antd';
import { clearAuth } from './utils/auth';

// Routes that don't need a subscription check
const PUBLIC_PATHS = ['/login', '/register', '/onboarding', '/auth-callback', '/trial-success', '/verify-email'];

declare global {
  interface Window {
    dataLayer: any[];
  }
}

const useGTMPageView = () => {
  const location = useLocation();

  // useEffect(() => {
  //   TagManager.dataLayer({
  //     dataLayer: {
  //       event: 'pageview',
  //       page: location.pathname
  //     }
  //   });
  // }, [location]); // Runs every time location changes
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Global 403 handler ────────────────────────────────────────────────────
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const errorCode = error?.response?.data?.error;
        if (error?.response?.status === 403) {
          if (errorCode === 'account_locked') {
            // Full account lock — subscription expired. Force logout.
            clearAuth();
            message.error('Your account has been deactivated. Please re-subscribe to continue.', 5);
            navigate('/login');
          } else if (errorCode === 'subscription_cancelled') {
            // Cancelled but still in trial/period — block only new blog generation
            message.warning(
              'Your subscription has been cancelled. Re-subscribe to generate new blogs.',
              6
            );
            navigate('/account');
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);
  // ──────────────────────────────────────────────────────────────────────────

  // ── Global subscription gate ─────────────────────────────────────────────
  // Only the very first mount blocks rendering. Subsequent route changes
  // trigger a background re-check that redirects on failure but never
  // unmounts the UI — otherwise every sidebar click caused a white flash
  // for the duration of the /plan-details round-trip.
  const [initialSubCheckDone, setInitialSubCheckDone] = useState(false);
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('accessToken');
    const isOnboarding = location.pathname.startsWith('/onboarding');
    const isVerifyEmail = location.pathname.startsWith('/verify-email');
    const isOtherPublicPath = PUBLIC_PATHS
      .filter((p) => p !== '/onboarding')
      .some((p) => location.pathname.startsWith(p));

    if (!userId || !token || isOtherPublicPath) {
      if (!initialSubCheckDone) setInitialSubCheckDone(true);
      return;
    }

    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/plan-details?userId=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        // ── Email verification gate ──────────────────────────────────
        // If the user hasn't verified their email yet, force them to
        // /verify-email regardless of subscription status. This prevents
        // the browser back-button from bypassing email verification.
        if (res.data?.isEmailVerified === false && !isVerifyEmail) {
          navigate('/verify-email', { replace: true });
          return;
        }

        if (isOnboarding) {
          if (res.data?.hasSubscription === true) {
            navigate('/', { replace: true });
          }
        } else {
          if (res.data?.hasSubscription === false) {
            navigate('/onboarding', { replace: true });
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!initialSubCheckDone) setInitialSubCheckDone(true);
      });
  }, [location.pathname, navigate, initialSubCheckDone]);
  // ──────────────────────────────────────────────────────────────────────────


  const tagManagerArgs = {
    gtmId: String(process.env.REACT_APP_GTM_ID)
  };

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
    const handleBeforeUnload = (event: any) => {
      // Check if the user is navigating away (not a reload)
      const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (navigationEntry) {
      } else {
        console.log('Navigation Type not available');
      }
      setTimeout(() => {}, 5000);
      return;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  useGTMPageView();
  useGlobalButtonTracker();

  return (
    <div>
      <GlobalProvider>
        {!initialSubCheckDone ? null : (
          <Routes>
            <Route path='/auth-callback' element={<AuthHandler />} />
            {/* Public routes still wrap per-route — PublicLayout is cheap. */}
            {routes
              .filter((r: RouteInterface) => r.layout === 'public')
              .map((route: RouteInterface) => (
                <Route
                  key={route.id}
                  path={route.path}
                  element={<PublicLayout>{route.element}</PublicLayout>}
                />
              ))}
            {/* Private routes share one PrivateLayout instance via <Outlet />;
                Header, Sidebar, and alert bar persist across navigation. */}
            <Route element={<PrivateLayout />}>
              {routes
                .filter((r: RouteInterface) => r.layout !== 'public')
                .map((route: RouteInterface) => (
                  <Route key={route.id} path={route.path} element={route.element} />
                ))}
            </Route>
          </Routes>
        )}
      </GlobalProvider>
    </div>
  );
}

export default App;
